import React from 'react';

import { ActionIcon, UnstyledButton } from '@mantine/core';
import { Plus, Star, X } from 'lucide-react';

import { InfoTooltip } from '../../common/InfoTooltip';
import { type ExportResponse, type ExportTabId, MAX_EXPORT_TABS, SELECTED_TAB_ID } from '../types';
import { formatExportTimestamp } from '../utilities/exportSummaryUtils';

const s = {
  strip: 'flex items-end gap-8 w-full px-6 border-0 border-b border-solid border-[#e1e5e9]',
  tabGroup: 'group relative flex items-start',
  tab: 'relative flex flex-col items-start gap-0.5 py-3 bg-transparent border-none cursor-pointer',
  tabLabel: 'text-sm leading-tight',
  tabLabelActive: 'font-semibold text-foreground',
  tabLabelIdle: 'font-medium text-[#6b7772]',
  tabTimestamp: 'text-xs text-[#8a938e]',
  indicator: 'absolute inset-x-0 -bottom-px h-0.5 bg-[#e8590c]',
  close: 'ml-1 mt-3 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity',
  closeIcon: 'w-3.5 h-3.5',
  selectedTab: 'relative flex flex-col items-start justify-center self-stretch shrink-0 py-3 bg-transparent border-none cursor-pointer',
  selectedRow: 'flex items-center gap-1.5 whitespace-nowrap px-2 py-1 -my-1 rounded-md',
  selectedRowPulse: 'motion-safe:animate-pulse-highlight motion-reduce:animate-hold-highlight',
  star: 'w-3.5 h-3.5',
  add: 'ml-4 self-center',
} as const;

const ADD_EXPORT_HINTS = {
  atCap: `Maximum of ${MAX_EXPORT_TABS} exports reached`,
  nothingSelected: 'Select clonotypes and sequences on Hit selection to create an export',
  ready: 'New export from the current selection',
} as const;

function addExportHint(atCap: boolean, hasSelection: boolean): string {
  if (atCap) {
    return ADD_EXPORT_HINTS.atCap;
  }
  return hasSelection ? ADD_EXPORT_HINTS.ready : ADD_EXPORT_HINTS.nothingSelected;
}

interface ExportTabStripProps {
  exports: ExportResponse[];
  activeTabId: ExportTabId;
  selectedCount: number;
  /**
   * Content signature of the Selected set — changes only when the selection itself changes.
   * A count alone would miss a swap (one sequence unticked, another ticked).
   */
  selectionSignature: string;
  isLoading: boolean;
  onTabChange: (tabId: ExportTabId) => void;
  onCloseExport: (exportId: string) => void;
  onAddExport: () => void;
}

export function ExportTabStrip({
  exports,
  activeTabId,
  selectedCount,
  selectionSignature,
  isLoading,
  onTabChange,
  onCloseExport,
  onAddExport,
}: ExportTabStripProps) {
  const isSelectedActive = activeTabId === SELECTED_TAB_ID;
  const atCap = exports.length >= MAX_EXPORT_TABS;

  /*
   * The Selected list changes while the user is looking at an Export tab, so highlight it whenever
   * what it summarises moves.
   */
  const [pulseKey, setPulseKey] = React.useState(0);
  const previousSelectionSignature = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (isLoading) {
      return;
    }
    const previous = previousSelectionSignature.current;
    previousSelectionSignature.current = selectionSignature;
    if (previous === null || previous === selectionSignature) {
      return;
    }
    setPulseKey((current) => current + 1);
  }, [selectionSignature, isLoading]);

  return (
    <div className={s.strip} role="tablist" aria-label="Export tabs">
      {exports.map((exportTab) => {
        const isActive = activeTabId === exportTab.exportId;
        return (
          <div key={exportTab.exportId} className={s.tabGroup}>
            <UnstyledButton role="tab" aria-selected={isActive} className={s.tab} onClick={() => onTabChange(exportTab.exportId)}>
              <span className={`${s.tabLabel} ${isActive ? s.tabLabelActive : s.tabLabelIdle}`}>Export {exportTab.exportNumber}</span>
              <span className={s.tabTimestamp}>{formatExportTimestamp(exportTab.createdAt)}</span>
              {isActive && <span className={s.indicator} />}
            </UnstyledButton>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="xs"
              className={s.close}
              aria-label={`Close export ${exportTab.exportNumber}`}
              onClick={() => onCloseExport(exportTab.exportId)}
            >
              <X className={s.closeIcon} />
            </ActionIcon>
          </div>
        );
      })}

      {/* The "Selected" tab — every sequence ticked across all export tabs, de-duplicated.
          Placed last to match the Figma mock */}
      <UnstyledButton role="tab" aria-selected={isSelectedActive} className={s.selectedTab} onClick={() => onTabChange(SELECTED_TAB_ID)}>
        <span key={pulseKey} className={`${s.selectedRow} ${pulseKey > 0 ? s.selectedRowPulse : ''}`} onAnimationEnd={() => setPulseKey(0)}>
          <Star className={s.star} fill="currentColor" />
          <span className={`${s.tabLabel} ${isSelectedActive ? s.tabLabelActive : s.tabLabelIdle}`}>Selected ({selectedCount})</span>
        </span>
        {isSelectedActive && <span className={s.indicator} />}
      </UnstyledButton>

      <InfoTooltip label={addExportHint(atCap, selectedCount > 0)}>
        <span className={s.add}>
          <ActionIcon variant="subtle" color="gray" size="sm" onClick={onAddExport} disabled={atCap || selectedCount === 0} aria-label="Add export">
            <Plus className={s.closeIcon} />
          </ActionIcon>
        </span>
      </InfoTooltip>
    </div>
  );
}
